import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';

class ReportExporter {
    exportToPDF = async (reportData) => {
        // Create HTML content for PDF
        let htmlContent = `
            <html>
            <head>
                <meta charset="UTF-8">
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; color: #333; }
                    h1 { color: #2563eb; text-align: center; margin-bottom: 20px; }
                    .summary { border: 1px solid #ddd; padding: 15px; margin: 20px 0; border-radius: 5px; background: #f9fafb; }
                    .summary-row { margin: 8px 0; }
                    .summary-label { font-weight: bold; display: inline-block; width: 150px; }
                    .question-section { page-break-before: always; margin-top: 30px; margin-bottom: 20px; }
                    .question-title { color: #2563eb; font-size: 16px; font-weight: bold; margin: 15px 0 10px 0; }
                    .location-title { color: #374151; font-size: 14px; font-weight: bold; margin: 12px 0 8px 0; }
                    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                    th { background-color: #3b82f6; color: white; padding: 10px; text-align: left; border: 1px solid #3b82f6; }
                    td { padding: 10px; border: 1px solid #e5e7eb; }
                    tr:nth-child(even) { background-color: #f3f4f6; }
                    .text-center { text-align: center; }
                </style>
            </head>
            <body>
                <h1>Form Raporu</h1>
                <div class="summary">
                    <div class="summary-row"><span class="summary-label">Form:</span> ${this.escapeHtml(reportData.formName)}</div>
                    <div class="summary-row"><span class="summary-label">Toplam Yanıt:</span> ${reportData.totalSubmissions}</div>
                    <div class="summary-row"><span class="summary-label">Tarih:</span> ${new Date().toLocaleDateString('tr-TR')}</div>
                </div>
        `;

        // Add questions
        reportData.questions.forEach((question, qIdx) => {
            htmlContent += `
                <div style="margin-bottom: 20px;"></div>
                <div class="question-section">
                    <div class="question-title">Soru ${qIdx + 1}: ${this.escapeHtml(question.questionText)}</div>
            `;

            const locations = Object.values(question.answersByLocation);

            if (locations.length > 1) {
                const aggregateStats = this.computeAggregateStats(locations);
                htmlContent += `
                    <div class="location-title">Genel İstatistikler (Tüm Lokasyonlar) — ${aggregateStats.totalAnswers} yanıt</div>
                `;
                if (question.questionType === 3) {
                    htmlContent += this.createMultiChoiceHtml(aggregateStats.multiChoice);
                } else if (question.questionType === 2) {
                    htmlContent += this.createStarRatingHtml(aggregateStats.starRating);
                } else if (question.questionType === 1) {
                    htmlContent += this.createFreeTextHtml(aggregateStats.freeText);
                }
            }

            locations.forEach(location => {
                htmlContent += `
                    <div class="location-title">${this.escapeHtml(location.locationName)} (${location.stats.totalAnswers} yanıt)</div>
                `;

                if (question.questionType === 3) {
                    // Multi-choice
                    htmlContent += this.createMultiChoiceHtml(location.stats.multiChoice);
                } else if (question.questionType === 2) {
                    // Star rating
                    htmlContent += this.createStarRatingHtml(location.stats.starRating);
                } else if (question.questionType === 1) {
                    // Free text
                    htmlContent += this.createFreeTextHtml(location.stats.freeText);
                }
            });

            htmlContent += `</div>`;
        });

        htmlContent += `
            </body>
            </html>
        `;

        const element = document.createElement('div');
        element.innerHTML = htmlContent;

        const opt = {
            margin: 10,
            filename: `rapor_${reportData.formId}_${new Date().getTime()}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4', compress: true },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        };

        try {
            html2pdf().set(opt).from(htmlContent).save();
        } catch (err) {
            console.error('PDF generation error:', err);
            alert('PDF oluşturulurken bir hata oluştu. Lütfen Excel formatını deneyin.');
        }
    };

    computeAggregateStats = (locations) => {
        return locations.reduce((acc, location) => {
            const { stats } = location;
            acc.totalAnswers += stats.totalAnswers || 0;
            Object.entries(stats.multiChoice || {}).forEach(([k, v]) => {
                acc.multiChoice[k] = (acc.multiChoice[k] || 0) + v;
            });
            Object.entries(stats.starRating || {}).forEach(([k, v]) => {
                acc.starRating[k] = (acc.starRating[k] || 0) + v;
            });
            Object.entries(stats.freeText || {}).forEach(([k, v]) => {
                acc.freeText[k] = (acc.freeText[k] || 0) + v;
            });
            return acc;
        }, { totalAnswers: 0, multiChoice: {}, starRating: {}, freeText: {} });
    };

    escapeHtml = (text) => {
        if (!text) return text;
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
        };
        return text.replace(/[&<>"']/g, m => map[m]);
    };

    createMultiChoiceHtml = (data) => {
        let html = `
            <table>
                <tr>
                    <th style="text-align: left;">Seçenek</th>
                    <th class="text-center" style="width: 100px;">Adet</th>
                </tr>
        `;

        Object.entries(data).forEach(([option, count]) => {
            html += `
                <tr>
                    <td>${this.escapeHtml(option.toString())}</td>
                    <td class="text-center">${count}</td>
                </tr>
            `;
        });

        html += `</table>`;
        return html;
    };

    createStarRatingHtml = (data) => {
        let html = `
            <table>
                <tr>
                    <th style="text-align: left;">Puan</th>
                    <th class="text-center" style="width: 100px;">Adet</th>
                </tr>
        `;

        for (let i = 1; i <= 5; i++) {
            html += `
                <tr>
                    <td>${i} Yıldız</td>
                    <td class="text-center">${data[i] || 0}</td>
                </tr>
            `;
        }

        const total = Object.values(data).reduce((sum, count) => sum + count, 0);
        const average = total > 0
            ? (Object.entries(data).reduce((sum, [stars, count]) => sum + (parseInt(stars) * count), 0) / total).toFixed(2)
            : 0;

        html += `
            <tr style="font-weight: bold;">
                <td>Ortalama</td>
                <td class="text-center">${average}</td>
            </tr>
        `;

        html += `</table>`;
        return html;
    };

    createFreeTextHtml = (data) => {
        let html = `
            <table>
                <tr>
                    <th style="text-align: left;">Yanıt</th>
                    <th class="text-center" style="width: 100px;">Adet</th>
                </tr>
        `;

        Object.entries(data)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 50)
            .forEach(([text, count]) => {
                html += `
                    <tr>
                        <td>${this.escapeHtml(text.substring(0, 100))}</td>
                        <td class="text-center">${count}</td>
                    </tr>
                `;
            });

        html += `</table>`;
        return html;
    };

    exportToXLSX = async (reportData) => {
        const workbook = XLSX.utils.book_new();

        // Summary sheet
        const summaryData = [
            ['Form Raporu'],
            [],
            ['Form:', reportData.formName],
            ['Toplam Yanıt:', reportData.totalSubmissions],
            ['Tarih:', new Date().toLocaleDateString('tr-TR')],
        ];
        const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWS, 'Özet');

        // Questions sheet
        reportData.questions.forEach((question, qIdx) => {
            const questionData = [];
            questionData.push([`Soru ${qIdx + 1}: ${question.questionText}`]);
            questionData.push([]);

            const locations = Object.values(question.answersByLocation);

            if (locations.length > 1) {
                const aggregateStats = this.computeAggregateStats(locations);
                questionData.push([`Genel İstatistikler (Tüm Lokasyonlar)`, `Yanıt: ${aggregateStats.totalAnswers}`]);
                questionData.push([]);
                if (question.questionType === 3) {
                    questionData.push(['Seçenek', 'Adet']);
                    Object.entries(aggregateStats.multiChoice).forEach(([option, count]) => {
                        questionData.push([option, count]);
                    });
                } else if (question.questionType === 2) {
                    questionData.push(['Puan', 'Adet']);
                    for (let i = 1; i <= 5; i++) {
                        questionData.push([`${i} Yıldız`, aggregateStats.starRating[i] || 0]);
                    }
                    const total = Object.values(aggregateStats.starRating).reduce((sum, c) => sum + c, 0);
                    const average = total > 0 ? (Object.entries(aggregateStats.starRating).reduce((sum, [stars, c]) => sum + (parseInt(stars) * c), 0) / total).toFixed(2) : 0;
                    questionData.push(['Ortalama', average]);
                } else if (question.questionType === 1) {
                    questionData.push(['Yanıt', 'Adet']);
                    Object.entries(aggregateStats.freeText)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 50)
                        .forEach(([text, count]) => {
                            questionData.push([text.substring(0, 100), count]);
                        });
                }
                questionData.push([]);
            }

            locations.forEach(location => {
                questionData.push([`${location.locationName}`, `Yanıt: ${location.stats.totalAnswers}`]);
                questionData.push([]);

                if (question.questionType === 3) {
                    // Multi-choice
                    questionData.push(['Seçenek', 'Adet']);
                    Object.entries(location.stats.multiChoice).forEach(([option, count]) => {
                        questionData.push([option, count]);
                    });
                } else if (question.questionType === 2) {
                    // Star rating
                    questionData.push(['Puan', 'Adet']);
                    for (let i = 1; i <= 5; i++) {
                        questionData.push([`${i} Yıldız`, location.stats.starRating[i] || 0]);
                    }
                    const total = Object.values(location.stats.starRating).reduce((sum, count) => sum + count, 0);
                    const average = total > 0 ? (Object.entries(location.stats.starRating).reduce((sum, [stars, count]) => sum + (parseInt(stars) * count), 0) / total).toFixed(2) : 0;
                    questionData.push(['Ortalama', average]);
                } else if (question.questionType === 1) {
                    // Free text
                    questionData.push(['Yanıt', 'Adet']);
                    Object.entries(location.stats.freeText)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 50)
                        .forEach(([text, count]) => {
                            questionData.push([text.substring(0, 100), count]);
                        });
                }

                questionData.push([]);
            });

            const ws = XLSX.utils.aoa_to_sheet(questionData);
            ws['!cols'] = [{ wch: 50 }, { wch: 15 }];
            XLSX.utils.book_append_sheet(workbook, ws, `Soru ${qIdx + 1}`);
        });

        // Save Excel
        XLSX.writeFile(workbook, `rapor_${reportData.formId}_${new Date().getTime()}.xlsx`);
    };
}

export default ReportExporter;
